// schema
import * as ISchema from "@schema";
// libraries
import React, { ContextType, useContext } from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// event-summary-attendees
import { eventSummaryAutopilotFragments } from "./EventSummaryAutopilot.gql";
import { EventSummaryRow as s } from "./EventSummary.styles";
// state
import { IReduxState } from "@reducers/root.reducer";
import { setPricingDialogOpen } from "@actions/payments.actions";
// util
import { EFlex, getFlexFromEvent } from "@util/event-tag.util";
import { getNextDefragText } from "@clockwise/client-commons/src/util/defrag";
// material-ui
import { Assistant } from "@material-ui/icons";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { getValue } from "@util/errorable.util";

// ~-~-~-~-~-~-~-
// Types
// ~-~-~-~-~-~-~-
enum AutopilotSummaryState {
  Unavailable,
  On,
  Off,
  Paused,
  FreemiumOrgPaused,
}

// const iconColorForState = {
//   [AutopilotSummaryState.Off]: ,
//   [AutopilotSummaryState.Off]: ,
//   [AutopilotSummaryState.Off]: ,
//   [AutopilotSummaryState.Off]: ,
// }

interface IContainer {
  autopilotPaused?: boolean;
  canAutopilot?: boolean;
  event: ISchema.IEvent | null;
  flex?: EFlex;
  fromSmartHold?: boolean;
  nextDefragTime?: number;
  org: ISchema.IOrg | null;
}

interface IProps extends IContainer {
  setPricingDialogOpen: typeof setPricingDialogOpen;
}

// ~-~-~-~-~-~-~-
// Helpers
// ~-~-~-~-~-~-~-
function getIconColorStyle(state: AutopilotSummaryState, fromSmartHold?: boolean) {
  if (fromSmartHold) {
    return s.iconProminent;
  }

  switch (state) {
    case AutopilotSummaryState.On:
      return s.iconProminent;
    case AutopilotSummaryState.Paused:
    case AutopilotSummaryState.FreemiumOrgPaused:
      return s.iconWarning;
    default:
    case AutopilotSummaryState.Off:
      return s.iconDisabled;
  }
}

// ~-~-~-~-~-~-~-
// Render
// ~-~-~-~-~-~-~-
function getSummaryText(
  state: AutopilotSummaryState,
  t: ContextType<typeof TranslationContext>,
  nextDefragTime?: number,
  autopilotStatus?: ISchema.AutopilotEventStatusEnum | undefined,
) {
  if (autopilotStatus === ISchema.AutopilotEventStatusEnum.SmartHold) {
    return <div style={s.textProminent}>SMART HOLD</div>;
  }

  let autopilotText = null;
  if (autopilotStatus === ISchema.AutopilotEventStatusEnum.OverQuota) {
    autopilotText = <div style={s.text}>Free plan quota exceeded</div>;
  } else if (nextDefragTime) {
    autopilotText = (
      <div style={s.text}>
        {t("event-summary:next-autopilot-run-time", {
          nextDefragRunTime: getNextDefragText(nextDefragTime),
        })}
      </div>
    );
  }

  switch (state) {
    case AutopilotSummaryState.On:
      return (
        <div style={s.textProminent}>
          {t("event:autopilot-label-on")}
          {autopilotText}
        </div>
      );
    case AutopilotSummaryState.Off:
      return (
        <div style={s.textDisabled}>
          {t("event:autopilot-label-off")}
          {autopilotText}
        </div>
      );
    case AutopilotSummaryState.Paused:
    case AutopilotSummaryState.FreemiumOrgPaused:
      return (
        <div style={s.textWarning}>
          {t("event:autopilot-label-paused")}
          {autopilotText}
        </div>
      );
    case AutopilotSummaryState.Unavailable:
    default:
      return <div />;
  }
}

// ~-~-~-~-~-~-~-
// Component
// ~-~-~-~-~-~-~-
const EventSummaryAutopilotCmpt = (props: IProps) => {
  const t = useContext(TranslationContext);
  const { event, flex, fromSmartHold, canAutopilot, nextDefragTime } = props;

  if (!flex && !event) {
    return null;
  }

  // TODO: if we figure out if the `AutopilotOnboardingDND` experiment is fully turned off
  // we'll be able to remove the `currentFlex` logic and simplify this
  const currentFlex = flex || getFlexFromEvent(event!, true);
  const autopilotStatus = getValue(event?.autopilotEventStatus)?.status;
  const isCurrentFlexPinned = currentFlex == EFlex.Pin;
  const isAutopilotStatusPinned = autopilotStatus === ISchema.AutopilotEventStatusEnum.Pinned;
  const isPaused = autopilotStatus === ISchema.AutopilotEventStatusEnum.Paused;
  const isPinned = flex ? isCurrentFlexPinned : isAutopilotStatusPinned;

  let state: AutopilotSummaryState = AutopilotSummaryState.Unavailable;
  if (isPaused && !isPinned && !fromSmartHold) {
    state = AutopilotSummaryState.Paused;
  } else if (!isPinned || fromSmartHold) {
    state = AutopilotSummaryState.On;
  } else if (isPinned && canAutopilot) {
    state = AutopilotSummaryState.Off;
  }

  if (state === AutopilotSummaryState.Unavailable) {
    return <span />;
  }

  if (autopilotStatus === ISchema.AutopilotEventStatusEnum.OverQuota) {
    state = AutopilotSummaryState.FreemiumOrgPaused;
  }

  return (
    <div style={s.container} cw-id="meeting-tag">
      <Assistant style={getIconColorStyle(state, fromSmartHold)} />
      {getSummaryText(state, t, nextDefragTime, autopilotStatus)}
    </div>
  );
};

///////////
// Redux //
///////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const EventSummaryAutopilotRedux = connect(mapStateToProps, {
  setPricingDialogOpen,
})(EventSummaryAutopilotCmpt);

///////////
// Relay //
///////////
export const EventSummaryAutopilot = createFragmentContainer<IContainer>(
  EventSummaryAutopilotRedux,
  eventSummaryAutopilotFragments,
);
